import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useWatch } from "react-hook-form";
import { Navigate, useNavigate } from "react-router-dom";
import { t } from "ttag";

import { EVENT_PREFIX } from "@/features/analytics/models";
import { useCustomerIoContext } from "@/features/analytics/providers";
import { trackDqPageViewed } from "@/features/analytics/trackers/dq-page-viewed";
import { useDynamicForm } from "@/features/forms/hooks";
import { FIELD_TYPE } from "@/features/forms/models";
import type { Answers, Form as DynamicFormType } from "@/features/forms/models";
import {
  evaluateFilterCondition,
  filterHiddenConditionalAnswers,
} from "@/features/forms/utils";
import { LogoLoader } from "@/shared/components";
import { errorToast, Form, SectionHeader } from "@/shared/ui";
import { calculateBmi } from "@/shared/utils";
import { questionnaireQueriesStore } from "../api";
import {
  IntakeQuestion,
  QuestionNavigation,
  QuestionStep,
} from "../components";
import { QUESTIONNAIRE_ROUTE } from "../router/constants";
import { useQuestionnaireStore } from "../stores";

type Field = DynamicFormType["fields"][number];

interface QuestionGroup {
  lead: Field;
  children: Field[];
}

const READ_ONLY_TYPES: string[] = [
  FIELD_TYPE.READ_ONLY,
  FIELD_TYPE.READ_ONLY_LINK,
];
const HIDDEN_FIELD_LABELS = ["async_visit", "sync_visit"];
const VALID_TYPES: string[] = Object.values(FIELD_TYPE);

const isRenderable = (field: Field): boolean => {
  if (field.type === FIELD_TYPE.HIDDEN || !VALID_TYPES.includes(field.type)) {
    return false;
  }
  if (
    READ_ONLY_TYPES.includes(field.type) &&
    HIDDEN_FIELD_LABELS.includes(field.label)
  ) {
    return false;
  }
  return true;
};

/**
 * Groups each parent question with the conditional questions that depend on it
 * so they are shown together on the same step. A conditional field is attached
 * to the group of the field it depends on (following nested dependencies).
 */
const buildGroups = (fields: Field[]): QuestionGroup[] => {
  const groups: QuestionGroup[] = [];
  const groupByFieldId = new Map<string, QuestionGroup>();

  for (const field of fields) {
    if (!isRenderable(field)) {
      continue;
    }

    const condition = field.customModuleCondition;
    const parentGroup = condition
      ? groupByFieldId.get(condition.conditionalCustomModuleId)
      : undefined;

    if (parentGroup) {
      parentGroup.children.push(field);
      groupByFieldId.set(field.id, parentGroup);
      continue;
    }

    const group: QuestionGroup = { lead: field, children: [] };
    groups.push(group);
    groupByFieldId.set(field.id, group);
  }

  return groups;
};

const isChildVisible = (child: Field, values: Answers): boolean => {
  const condition = child.customModuleCondition;
  if (!condition) {
    return true;
  }
  const dependency = String(values[condition.conditionalCustomModuleId] ?? "");
  return evaluateFilterCondition(
    condition.filterType,
    dependency,
    condition.valueToFilter,
  );
};

export const Intake = () => {
  const navigate = useNavigate();
  const { analytics } = useCustomerIoContext();
  const heightFeet = useQuestionnaireStore((store) => store.heightFeet);
  const heightInches = useQuestionnaireStore((store) => store.heightInches);
  const initialWeight = useQuestionnaireStore((store) => store.initialWeight);
  const gender = useQuestionnaireStore((store) => store.gender);
  const state = useQuestionnaireStore((store) => store.state);
  const setIntake = useQuestionnaireStore((store) => store.setIntake);

  const bmi = calculateBmi({
    heightFeet,
    heightInches,
    weightInPounds: initialWeight,
  });

  const {
    data: intakeForm,
    isLoading,
    isSuccess,
    isError,
  } = useQuery({
    ...questionnaireQueriesStore.intake.form({ bmi, state }),
    enabled: bmi > 0,
  });

  const form = useDynamicForm(intakeForm);

  const groups = useMemo(
    () => (intakeForm ? buildGroups(intakeForm.fields) : []),
    [intakeForm],
  );

  const [groupIndex, setGroupIndex] = useState(0);

  // Reactive form values, used to reveal conditional children as they qualify.
  const watchedValues = useWatch({ control: form.control }) as Answers;

  useEffect(() => {
    if (isSuccess) {
      setGroupIndex(0);
    }
  }, [isSuccess]);

  useEffect(() => {
    trackDqPageViewed({
      customerIoAnalytics: analytics,
      eventPrefix: EVENT_PREFIX.QUESTIONNAIRE,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isError) {
      errorToast(t`We couldn't load your health questions. Please try again.`);
    }
  }, [isError]);

  if (!gender) {
    return <Navigate to={QUESTIONNAIRE_ROUTE.PERSONAL_INFO} replace />;
  }

  if (bmi <= 0) {
    return <Navigate to={QUESTIONNAIRE_ROUTE.MEASUREMENTS} replace />;
  }

  const currentGroup = groups[groupIndex];

  const visibleFields = currentGroup
    ? [
        currentGroup.lead,
        ...currentGroup.children.filter((child) =>
          isChildVisible(child, watchedValues),
        ),
      ]
    : [];

  const finish = () => {
    if (!intakeForm) {
      return;
    }
    const answers = filterHiddenConditionalAnswers(
      form.getValues() as Answers,
      intakeForm.fields,
    );
    setIntake({ intakeFormId: intakeForm.formId, intakeAnswers: answers });
    navigate(QUESTIONNAIRE_ROUTE.PERSONAL_DETAILS);
  };

  const goToNextGroup = () => {
    if (groupIndex >= groups.length - 1) {
      return finish();
    }
    setGroupIndex((index) => index + 1);
  };

  const validateVisibleFields = async () => {
    const values = form.getValues() as Answers;
    const currentlyVisible = currentGroup
      ? [
          currentGroup.lead,
          ...currentGroup.children.filter((child) =>
            isChildVisible(child, values),
          ),
        ]
      : [];
    return form.trigger(currentlyVisible.map((field) => field.id) as never);
  };

  const handleContinue = async () => {
    if (!groups.length) {
      return finish();
    }
    const isValid = await validateVisibleFields();
    if (isValid) {
      goToNextGroup();
    }
  };

  const handleAnswer = async (fieldId: string, autoAdvance: boolean) => {
    if (!autoAdvance || !currentGroup) {
      return;
    }

    const values = form.getValues() as Answers;
    const currentlyVisible = [
      currentGroup.lead,
      ...currentGroup.children.filter((child) => isChildVisible(child, values)),
    ];

    const answeredIsLast =
      currentlyVisible[currentlyVisible.length - 1]?.id === fieldId;

    // If answering revealed a follow-up question, stay so it can be answered.
    if (!answeredIsLast) {
      return;
    }

    const isValid = await form.trigger(
      currentlyVisible.map((field) => field.id) as never,
    );
    if (isValid) {
      goToNextGroup();
    }
  };

  const handleBack = () => {
    if (groupIndex === 0) {
      return navigate(QUESTIONNAIRE_ROUTE.PERSONAL_INFO);
    }
    setGroupIndex((index) => index - 1);
  };

  return (
    <Form.Provider {...form}>
      <QuestionStep
        onSubmit={() => void handleContinue()}
        footer={<QuestionNavigation onBack={handleBack} />}
      >
        <LogoLoader show={isLoading} fullscreen />

        <SectionHeader title={t`A few health questions`} />

        <div className="flex w-full flex-col gap-8 sm:max-w-xl">
          {visibleFields.map((field) => (
            <IntakeQuestion
              key={field.id}
              field={field}
              onAnswer={handleAnswer}
            />
          ))}
        </div>
      </QuestionStep>
    </Form.Provider>
  );
};
